import type { BuildingCategory, BuildingInStoreInfo, FloorInfo } from '@/services/dto';
import { POI_DISPLAY_TYPE } from '@/enums';
import shopUtils, { getListCategories, getSearchMenu, isDutyFreePoi, resolveShopPoiContents } from '@/utils/shop';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { useConfigStore } from './configStore';
import type { TFunction } from 'i18next';
import { SHOP_SEARCH_MODE } from '@/enums/shop';
import type { ShopState, ShopActions, ShopFilterConfig } from '@/stores/types/shopStore';
import type { PoiContentSlide } from '@/types/shop';
import { STORE_CODE_MAP_JAMSIL } from '@/constants/app';
import { isDutyFreeStoreCode } from '@/utils/floor-guide';

const optionAllCategory: BuildingCategory = {
    clientCode: 'all',
    name: {
        ko: 'common.text.all_category',
        en: 'common.text.all_category',
        ja: 'common.text.all_category',
        'zh-CN': 'common.text.all_category',
        'zh-TW': 'common.text.all_category',
    },
    id: '',
    parentClientCode: '',
    indentLevel: 0,
    categoryCode: '',
    parentCategoryCode: '',
};

const resolveDefaultBuildingId = (
    buildings: BuildingInStoreInfo[] | undefined,
    buildingMenus: Record<string, any> | null | undefined,
    language: string,
    storeCode?: string | null
): string => {
    if (!buildings?.length) return 'all';
    if (buildings.length > 1) return 'all';

    if (storeCode === STORE_CODE_MAP_JAMSIL) return 'all';

    const searchMenu = getSearchMenu(buildingMenus, language);
    const pois = searchMenu?.poiContents ?? [];
    const hasDutyFree = pois.some((poi) => isDutyFreePoi(poi, language));
    if (hasDutyFree) return 'all';

    return buildings[0].id;
};

interface ShopStore extends ShopState, ShopActions {}

export const useShopStore = create<ShopStore>()(
    devtools(
        (set, get) => ({
            shopContents: [],
            rawShopContents: [],
            searchText: '',
            selectedFloor: {
                id: 'all',
                label: '',
                value: '',
                data: null,
            },
            selectedBuildingId: 'all',
            selectedCategory: null,
            t: null,
            currentMode: SHOP_SEARCH_MODE.SEARCH_BY_TEXT,
            keyboardLanguage: 'ko',

            setKeyboardLanguage: (language: 'ko' | 'en') => {
                const prev = get().keyboardLanguage;
                if (prev === language) return;

                set({ keyboardLanguage: language });
            },

            setCurrentMode: (mode: SHOP_SEARCH_MODE) => {
                const prev = get().currentMode;
                if (prev === mode) return;

                set({ currentMode: mode });
            },

            setSearchText: (text: string) => {
                const prev = get().searchText;
                if (prev === text) return;

                set({ searchText: text });
            },

            setSelectedFloor: (floor: { id: string; label: string; value: string; data: FloorInfo | null | string[] } | null) => {
                const prev = get().selectedFloor;
                if (prev?.id === floor?.id) return;

                set({ selectedFloor: floor });
            },

            setSelectedBuildingId: (buildingId: string | null) => {
                const prev = get().selectedBuildingId;
                if (prev === buildingId) return;

                set({ selectedBuildingId: buildingId });
            },

            setSelectedCategory: (category: BuildingCategory) => {
                const prev = get().selectedCategory;
                if (prev?.clientCode === category?.clientCode) return;

                set({ selectedCategory: category });
            },

            setTranslationFunction: (t: TFunction<'translation', undefined>) => {
                set({ t });
            },

            resetFilters: () => {
                const state = get();
                const { t } = state;
                const { config } = useConfigStore.getState();
                const { server, ui } = config;

                const allFloorText = t ? t('shop.search.text.content.all_floor') : 'All Floors';
                const newBuildingId = resolveDefaultBuildingId(server?.store?.buildings, server?.buildingMenus, ui.language, server?.store?.storeCode);

                set({
                    searchText: '',
                    selectedFloor: {
                        id: 'all',
                        label: allFloorText,
                        value: 'all',
                        data: null,
                    },
                    selectedBuildingId: newBuildingId,
                    selectedCategory: optionAllCategory,
                });
            },

            updateShopContents: (searchMode: SHOP_SEARCH_MODE): PoiContentSlide[] => {
                const state = get();
                const { selectedFloor, selectedBuildingId, selectedCategory, searchText, keyboardLanguage } = state;
                const { config } = useConfigStore.getState();
                const {
                    ui: { language, mainLanguage },
                } = config;

                const allBuildings = config.server?.store?.buildings ?? [];
                if (!allBuildings.length) {
                    if (state.shopContents?.length > 0) {
                        set({ shopContents: [] });
                    }
                    return [];
                }
                const shopCategories = getListCategories(config.server?.buildingMenus, language, mainLanguage);
                const storeCode = config.server?.store?.storeCode ?? null;

                const searchMenu = getSearchMenu(config.server?.buildingMenus, language);
                const pois = resolveShopPoiContents(searchMenu, selectedBuildingId, searchMode);
                const buildingMenus = config.server?.buildingMenus;
                const rawShopContents = shopUtils.convertPoiContentToSlide(
                    language,
                    allBuildings,
                    pois.length ? pois : undefined,
                    searchText,
                    shopCategories,
                    buildingMenus,
                    storeCode
                );

                const searchMenuForFilter = getSearchMenu(config.server?.buildingMenus, language);
                const menuCategoryForFilter = searchMenuForFilter?.selectedCategories?.find((c) => c.clientCode === selectedCategory?.clientCode);
                const categoryCodesForFilter: string[] = menuCategoryForFilter?.mergeCategoryCode ?? [];

                const contentsPropertyManagement = config.server?.store?.contentsPropertyManagement ?? [];
                const keywordSearchEnabled = contentsPropertyManagement.some(
                    (item) => item.display_type === POI_DISPLAY_TYPE.KEY_WORDS && item.visible !== false
                );

                const filterConfig: ShopFilterConfig = {
                    language,
                    selectedBuildingId,
                    selectedFloor,
                    selectedCategory,
                    searchText,
                    buildings: config.server?.store?.buildings,
                    categoryCodesForFilter,
                    keywordSearchEnabled,
                };

                if (!rawShopContents?.length) {
                    if (state.shopContents?.length > 0) {
                        set({ shopContents: [] });
                    }
                    return [];
                }

                const shopFilter = shopUtils.createShopFilter(searchMode, filterConfig, keyboardLanguage);
                const filteredContents = rawShopContents.filter(shopFilter);

                if (filteredContents?.length === 0) {
                    if (state.shopContents?.length > 0) {
                        set({ shopContents: [] });
                    }
                    return [];
                }

                const getPriority = (char: string) => {
                    if (!char) return 4;
                    if (/[ㄱ-ㅎㅏ-ㅣ가-힣]/.test(char)) return 1;
                    if (/[a-zA-Z]/.test(char)) return 2;
                    if (/[0-9]/.test(char)) return 3;
                    return 4;
                };

                const shouldPrioritizeDutyFree =
                    (searchMode === SHOP_SEARCH_MODE.SEARCH_BY_TEXT && !!searchText?.trim()) ||
                    (searchMode === SHOP_SEARCH_MODE.SEARCH_BY_FLOOR && selectedFloor?.id !== 'all') ||
                    (searchMode === SHOP_SEARCH_MODE.SEARCH_BY_CATEGORY && selectedCategory?.clientCode !== 'all');

                const hasMixedBuildings =
                    shouldPrioritizeDutyFree &&
                    isDutyFreeStoreCode(storeCode) &&
                    filteredContents.some((item) => item.isDutyFreePoi) &&
                    filteredContents.some((item) => !item.isDutyFreePoi);

                const sortedContents = filteredContents.sort((a, b) => {
                    if (hasMixedBuildings) {
                        if (a.isDutyFreePoi && !b.isDutyFreePoi) return -1;
                        if (!a.isDutyFreePoi && b.isDutyFreePoi) return 1;
                    }

                    const nameA = (a?.data?.name?.[language] || '').trim();
                    const nameB = (b?.data?.name?.[language] || '').trim();

                    const priorityA = getPriority(nameA.charAt(0));
                    const priorityB = getPriority(nameB.charAt(0));

                    if (priorityA !== priorityB) {
                        return priorityA - priorityB;
                    }

                    return nameA.localeCompare(nameB, language, {
                        numeric: true,
                        sensitivity: 'base',
                        ignorePunctuation: true,
                    });
                });

                set({ shopContents: sortedContents });

                return sortedContents;
            },

            initialize: (t: TFunction<'translation', undefined>) => {
                const state = get();
                if (state.t === t && state.rawShopContents?.length > 0) {
                    return;
                }

                const { config } = useConfigStore.getState();
                const {
                    ui: { language, mainLanguage },
                    server,
                } = config;

                const allBuildings = server?.store?.buildings ?? [];
                const storeCode = server?.store?.storeCode ?? null;
                const selectedBuildingId = resolveDefaultBuildingId(allBuildings, server?.buildingMenus, language, storeCode);
                const shopCategories = getListCategories(server?.buildingMenus, language, mainLanguage);
                const searchMenu = getSearchMenu(server?.buildingMenus, language);
                const pois = resolveShopPoiContents(searchMenu, selectedBuildingId, SHOP_SEARCH_MODE.SEARCH_BY_TEXT);
                const buildingMenus = config.server?.buildingMenus;
                const rawShopContents = shopUtils.convertPoiContentToSlide(
                    language,
                    allBuildings,
                    pois.length ? pois : undefined,
                    undefined,
                    shopCategories,
                    buildingMenus,
                    storeCode
                );

                set({
                    t,
                    rawShopContents,
                    currentMode: SHOP_SEARCH_MODE.SEARCH_BY_TEXT,
                    selectedBuildingId,
                    selectedCategory: optionAllCategory,
                });
            },
        }),
        {
            name: 'shop-store',
        }
    )
);

let previousBuildings: BuildingInStoreInfo[] | undefined = undefined;
let previousLanguage: string = '';
let previousBuildingMenus: Record<string, unknown> | undefined = undefined;

useConfigStore.subscribe(() => {
    const { config } = useConfigStore.getState();
    const currentBuildings = config.server?.store?.buildings;
    const currentLanguage = config.ui?.language;
    const currentBuildingMenus = config.server?.buildingMenus as Record<string, unknown> | undefined;

    if (currentBuildings !== previousBuildings || currentLanguage !== previousLanguage || currentBuildingMenus !== previousBuildingMenus) {
        previousBuildings = currentBuildings;
        previousLanguage = currentLanguage;
        previousBuildingMenus = currentBuildingMenus;

        const { initialize, t } = useShopStore.getState();
        if (t) {
            initialize(t);
        }
    }
});
